import { ORDER_PRIMARY_PHASE_DIMENSIONS, ORDER_PRIMARY_PHASE_LINE_ITEM_DIMENSIONS, resolveOrderDimensionKey, resolveOrderDimensionLabel, resolveOrderLineItemDimensionBuckets } from '../../charts/pipeline/lib/orderPrimaryPhaseDimension';
import { getOrderStatisticsPrimaryPhase } from '../../charts/pipeline/lib/orderPrimaryPhaseConfig';
import { orderContainsStatsBrand } from '../../../../utils/order/orderStatsBrand';
import { getPrimaryPhaseRowMetricValue } from '../../charts/pipeline/lib/orderPrimaryPhaseMetric';
import { accumulatePrimaryPhaseOrderFinancials, accumulatePrimaryPhaseOrderFinancialsPartial, createEmptyPrimaryPhaseTotals } from '../../charts/pipeline/lib/primaryPhaseOrderFinancials';
import { buildStatisticsMaterialScopedOrder } from './statisticsMaterialLineContribution';
import { normalizeTrendAnchor } from './statisticsCompareConfig';
import { formatComparePeriodLabel, resolveComparePeriodChipLabel } from './statisticsComparePresets';
import { buildCalendarMonthRange, buildCalendarQuarterRange } from './statisticsPeriodQuickPickUtils';
import moment from 'moment';

export const TREND_DATE_FIELD = {
	CREATED_AT: 'createdAt',
	COMPLETED_AT: 'completedAt',
};

export const TREND_AXIS_MODE = {
	TIME: 'time',
	SALE_USER: 'saleUser',
	ASSISTANT_USER: 'assistantUser',
	CUSTOMER: 'customer',
	CUSTOMER_CONTACT: 'customerContact',
	PROVINCE: 'province',
	BRAND: 'brand',
	SUPPLIER: 'supplier',
	CATEGORY: 'category',
	PRODUCT_NAME: 'productName',
	PRODUCT_CODE: 'productCode',
};

/**
 * 横轴选项与顶栏可筛条件对齐（对象/物料维；时间轴由制作区单独选下单/完成日期）。
 * province 仍保留枚举供旧图/地图，但不出现在横轴选择里。
 */
export const TREND_AXIS_MODE_OPTIONS = [
	{ value: TREND_AXIS_MODE.TIME, label: '下单日期' },
	{ value: TREND_AXIS_MODE.CUSTOMER, label: '单位' },
	{ value: TREND_AXIS_MODE.CUSTOMER_CONTACT, label: '联系人' },
	{ value: TREND_AXIS_MODE.SALE_USER, label: '销售员' },
	{ value: TREND_AXIS_MODE.ASSISTANT_USER, label: '销售助理' },
	{ value: TREND_AXIS_MODE.SUPPLIER, label: '厂家' },
	{ value: TREND_AXIS_MODE.BRAND, label: '品牌' },
	{ value: TREND_AXIS_MODE.CATEGORY, label: '品类' },
	{ value: TREND_AXIS_MODE.PRODUCT_NAME, label: '品名' },
	{ value: TREND_AXIS_MODE.PRODUCT_CODE, label: '货号' },
];

const VALID_TREND_AXIS_MODE_VALUES = new Set(TREND_AXIS_MODE_OPTIONS.map((item) => item.value));

/** 可选横轴；旧值 province 等维度轴仍可渲染，但不出现在新选项里 */
export const normalizeTrendAxisMode = (mode) => {
	if (VALID_TREND_AXIS_MODE_VALUES.has(mode) || mode === TREND_AXIS_MODE.PROVINCE) return mode;
	return TREND_AXIS_MODE.TIME;
};

export const normalizeTrendDateField = (value) =>
	value === TREND_DATE_FIELD.COMPLETED_AT ? TREND_DATE_FIELD.COMPLETED_AT : TREND_DATE_FIELD.CREATED_AT;

const TREND_AXIS_TO_DIMENSION = {
	[TREND_AXIS_MODE.SALE_USER]: ORDER_PRIMARY_PHASE_DIMENSIONS.SALE_USER,
	[TREND_AXIS_MODE.ASSISTANT_USER]: ORDER_PRIMARY_PHASE_DIMENSIONS.ASSISTANT_USER,
	[TREND_AXIS_MODE.CUSTOMER]: ORDER_PRIMARY_PHASE_DIMENSIONS.CUSTOMER,
	[TREND_AXIS_MODE.CUSTOMER_CONTACT]: ORDER_PRIMARY_PHASE_DIMENSIONS.CUSTOMER_CONTACT,
	[TREND_AXIS_MODE.PROVINCE]: ORDER_PRIMARY_PHASE_DIMENSIONS.PROVINCE,
	[TREND_AXIS_MODE.BRAND]: ORDER_PRIMARY_PHASE_DIMENSIONS.BRAND,
	[TREND_AXIS_MODE.SUPPLIER]: ORDER_PRIMARY_PHASE_DIMENSIONS.SUPPLIER,
	[TREND_AXIS_MODE.CATEGORY]: ORDER_PRIMARY_PHASE_DIMENSIONS.CATEGORY,
	[TREND_AXIS_MODE.PRODUCT_NAME]: ORDER_PRIMARY_PHASE_DIMENSIONS.PRODUCT_NAME,
	[TREND_AXIS_MODE.PRODUCT_CODE]: ORDER_PRIMARY_PHASE_DIMENSIONS.PRODUCT_CODE,
};

const TREND_AXIS_FIELD_LABELS = {
	[TREND_AXIS_MODE.SALE_USER]: '销售员',
	[TREND_AXIS_MODE.ASSISTANT_USER]: '销售助理',
	[TREND_AXIS_MODE.CUSTOMER]: '单位',
	[TREND_AXIS_MODE.CUSTOMER_CONTACT]: '联系人',
	[TREND_AXIS_MODE.PROVINCE]: '省份',
	[TREND_AXIS_MODE.BRAND]: '品牌',
	[TREND_AXIS_MODE.SUPPLIER]: '厂家',
	[TREND_AXIS_MODE.CATEGORY]: '品类',
	[TREND_AXIS_MODE.PRODUCT_NAME]: '品名',
	[TREND_AXIS_MODE.PRODUCT_CODE]: '货号',
};

const DEFAULT_MAX_AXIS_POINTS = 12;
const CUSTOMER_MAX_AXIS_POINTS = 8;
const PROVINCE_MAX_AXIS_POINTS = 15;

const resolveDimensionMaxAxisPoints = (field) => {
	if (field === TREND_AXIS_MODE.CUSTOMER) return CUSTOMER_MAX_AXIS_POINTS;
	if (field === TREND_AXIS_MODE.PROVINCE) return PROVINCE_MAX_AXIS_POINTS;
	return DEFAULT_MAX_AXIS_POINTS;
};

const resolveLineCompareDimensionField = (field) => TREND_AXIS_TO_DIMENSION[field] ?? null;

const normalizeCustomerId = (customer) => {
	if (customer == null || customer === '') return '';
	if (typeof customer === 'string') return customer;
	if (typeof customer === 'object' && customer._id != null) return String(customer._id);
	return String(customer);
};

const resolveUserLabel = (userId, users = []) => {
	const match = users.find((user) => String(user._id) === String(userId));
	return match?.name?.trim() || '未知';
};

const parseDateKey = (value) => {
	if (!value) return null;
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? null : date;
};

const resolveOrderAxisDate = (order, dateField = TREND_DATE_FIELD.CREATED_AT) =>
	dateField === TREND_DATE_FIELD.COMPLETED_AT ? order?.completedAt : order?.createdAt;

const orderInDateRange = (order, startDate, endDate, dateField = TREND_DATE_FIELD.CREATED_AT) => {
	const axisDate = parseDateKey(resolveOrderAxisDate(order, dateField));
	if (!axisDate) return false;
	const start = parseDateKey(startDate);
	const end = parseDateKey(endDate);
	if (start && axisDate < start) return false;
	if (end) {
		const endDay = new Date(end);
		endDay.setHours(23, 59, 59, 999);
		if (axisDate > endDay) return false;
	}
	return true;
};

const resolveSaleUserFilterKeys = (filters = {}, saleUsers = []) =>
	(filters.saleUser ?? [])
		.filter(Boolean)
		.map((id) => ({
			key: String(id),
			label: resolveUserLabel(id, saleUsers),
		}));

const resolveCustomerFilterKeys = (filters = {}, customerOptions = []) =>
	(filters.customers ?? [])
		.filter(Boolean)
		.map((id) => ({
			key: String(id),
			label: customerOptions.find((item) => String(item.value) === String(id))?.label?.trim() || '未知客户',
		}));

const resolveBrandFilterKeys = (filters = {}) =>
	(filters.brands ?? [])
		.filter(Boolean)
		.map((brand) => {
			const label = String(brand).trim() || '未分类';
			return {
				key: `brand:${label}`,
				label,
			};
		});

const resolveContactFilterKeys = (filters = {}, customerContactOptions = []) =>
	(filters.customerContacts ?? [])
		.filter(Boolean)
		.map((id) => ({
			key: `contact:${id}`,
			label: customerContactOptions.find((item) => String(item.value) === String(id))?.label?.trim() || '未知联系人',
		}));

const resolveSupplierFilterKeys = (filters = {}) =>
	(filters.suppliers ?? [])
		.filter(Boolean)
		.map((name) => {
			const label = String(name).trim() || '未知厂家';
			return { key: `supplier:${label}`, label };
		});

const resolveCategoryFilterKeys = (filters = {}) =>
	(filters.categories ?? [])
		.filter(Boolean)
		.map((name) => {
			const label = String(name).trim() || '未分类';
			return { key: `category:${label}`, label };
		});

const orderMatchesSaleUserFilterKey = (order, pickKey, saleUsers = []) => {
	if (order?.saleUserId && String(order.saleUserId) === String(pickKey)) return true;
	const label = resolveUserLabel(pickKey, saleUsers);
	return Boolean(label && String(order.saleUserName ?? '').trim() === label);
};

const orderMatchesCustomerFilterKey = (order, pickKey) => normalizeCustomerId(order.customer) === String(pickKey);

const orderMatchesBrandFilterKey = (order, pickKey) => {
	const brand = String(pickKey).replace(/^brand:/, '');
	return orderContainsStatsBrand(order, brand);
};

const orderMatchesContactFilterKey = (order, pickKey) => {
	const id = String(pickKey).replace(/^contact:/, '');
	return String(order?.customerInfo?.staff_id ?? '') === id;
};

const orderMatchesSupplierFilterKey = (order, pickKey) => {
	const name = String(pickKey).replace(/^supplier:/, '');
	return (order?.orderItems ?? []).some((item) => String(item?.supplierName ?? '').trim() === name);
};

const orderMatchesCategoryFilterKey = (order, pickKey) => {
	const name = String(pickKey).replace(/^category:/, '');
	return (order?.orderItems ?? []).some((item) => String(item?.category ?? '').trim() === name);
};

const resolveAssistantUserFilterKeys = (filters = {}, assistantUsers = []) =>
	(filters.assistantUser ?? [])
		.filter(Boolean)
		.map((id) => ({
			key: String(id),
			label: resolveUserLabel(id, assistantUsers),
		}));

const orderMatchesAssistantUserFilterKey = (order, pickKey, assistantUsers = []) => {
	if (order?.assistantUserId && String(order.assistantUserId) === String(pickKey)) return true;
	const label = resolveUserLabel(pickKey, assistantUsers);
	return Boolean(label && String(order.assistantUserName ?? '').trim() === label);
};

const resolveDimensionFilterKeys = (field, filters, saleUsers, assistantUsers, customerOptions, customerContactOptions = []) => {
	if (field === TREND_AXIS_MODE.SALE_USER) {
		return resolveSaleUserFilterKeys(filters, saleUsers);
	}
	if (field === TREND_AXIS_MODE.ASSISTANT_USER) {
		return resolveAssistantUserFilterKeys(filters, assistantUsers);
	}
	if (field === TREND_AXIS_MODE.CUSTOMER) {
		return resolveCustomerFilterKeys(filters, customerOptions);
	}
	if (field === TREND_AXIS_MODE.CUSTOMER_CONTACT) {
		return resolveContactFilterKeys(filters, customerContactOptions);
	}
	if (field === TREND_AXIS_MODE.BRAND) {
		return resolveBrandFilterKeys(filters);
	}
	if (field === TREND_AXIS_MODE.SUPPLIER) {
		return resolveSupplierFilterKeys(filters);
	}
	if (field === TREND_AXIS_MODE.CATEGORY) {
		return resolveCategoryFilterKeys(filters);
	}
	return [];
};

const orderMatchesDimensionFilterKey = (order, field, pickKey, { saleUsers = [], assistantUsers = [] } = {}) => {
	if (field === TREND_AXIS_MODE.SALE_USER) {
		return orderMatchesSaleUserFilterKey(order, pickKey, saleUsers);
	}
	if (field === TREND_AXIS_MODE.ASSISTANT_USER) {
		return orderMatchesAssistantUserFilterKey(order, pickKey, assistantUsers);
	}
	if (field === TREND_AXIS_MODE.CUSTOMER) {
		return orderMatchesCustomerFilterKey(order, pickKey);
	}
	if (field === TREND_AXIS_MODE.CUSTOMER_CONTACT) {
		return orderMatchesContactFilterKey(order, pickKey);
	}
	if (field === TREND_AXIS_MODE.BRAND) {
		return orderMatchesBrandFilterKey(order, pickKey);
	}
	if (field === TREND_AXIS_MODE.SUPPLIER) {
		return orderMatchesSupplierFilterKey(order, pickKey);
	}
	if (field === TREND_AXIS_MODE.CATEGORY) {
		return orderMatchesCategoryFilterKey(order, pickKey);
	}
	return false;
};

/** 对比段含月/季元数据时，按实际月/季边界收窄统计区间 */
export const resolveDimensionPeriodDateBounds = (period = {}) => {
	if (!period?.startDate || !period?.endDate) return null;

	const year = moment(period.startDate).year();
	if (!Number.isFinite(year)) {
		return { startDate: period.startDate, endDate: period.endDate };
	}

	if (period.periodAxis === 'quarter' && period.quarters?.length) {
		const sorted = [...period.quarters].sort((a, b) => a - b);
		const first = buildCalendarQuarterRange(year, sorted[0]);
		const last = buildCalendarQuarterRange(year, sorted[sorted.length - 1]);
		return { startDate: first.startDate, endDate: last.endDate };
	}

	if (period.periodAxis === 'month' && period.months?.length) {
		const sorted = [...period.months].sort((a, b) => a - b);
		const first = buildCalendarMonthRange(year, sorted[0]);
		const last = buildCalendarMonthRange(year, sorted[sorted.length - 1]);
		return { startDate: first.startDate, endDate: last.endDate };
	}

	return { startDate: period.startDate, endDate: period.endDate };
};

const withDimensionPeriodDateBounds = (period = {}) => {
	const bounds = resolveDimensionPeriodDateBounds(period);
	if (!bounds) return period;
	return { ...period, startDate: bounds.startDate, endDate: bounds.endDate };
};

/** 基准 + 自定义对比段（用于维度横轴多年对比） */
export const resolveLineCompareDimensionPeriods = (filters = {}) => {
	const anchor =
		normalizeTrendAnchor(filters.trendAnchor) ??
		(filters.startDate && filters.endDate
			? normalizeTrendAnchor({ startDate: filters.startDate, endDate: filters.endDate, label: '' })
			: null);
	if (!anchor) return [];

	const periods = [
		withDimensionPeriodDateBounds({
			id: 'anchor',
			kind: 'anchor',
			label: resolveComparePeriodChipLabel(anchor) || '基准',
			startDate: anchor.startDate,
			endDate: anchor.endDate,
			periodAxis: anchor.periodAxis,
			months: anchor.months,
			quarters: anchor.quarters,
		}),
	];

	for (const period of filters.customComparePeriods ?? []) {
		if (!period?.startDate || !period?.endDate) continue;
		periods.push(
			withDimensionPeriodDateBounds({
				id: period.id ?? `custom-${period.startDate}-${period.endDate}`,
				kind: 'custom',
				label:
					resolveComparePeriodChipLabel({
						startDate: period.startDate,
						endDate: period.endDate,
						label: period.label,
						periodAxis: period.periodAxis,
						months: period.months,
						quarters: period.quarters,
					}) || formatComparePeriodLabel(period.startDate, period.endDate),
				startDate: period.startDate,
				endDate: period.endDate,
				periodAxis: period.periodAxis,
				months: period.months,
				quarters: period.quarters,
			}),
		);
	}

	return periods;
};

const resolveUnifiedDimensionAxis = (filterKeys, periodRowsList, maxPoints) => {
	if (filterKeys.length) {
		return filterKeys.map((item) => ({ key: item.key, label: item.label }));
	}

	const scoreMap = new Map();
	for (const rows of periodRowsList) {
		for (const row of rows) {
			const prev = scoreMap.get(row.key) ?? { key: row.key, label: row.label, score: 0 };
			prev.score += getPrimaryPhaseRowMetricValue(row, 'totalPrice');
			prev.label = prev.label || row.label;
			scoreMap.set(row.key, prev);
		}
	}

	let axis = [...scoreMap.values()].sort((a, b) => b.score - a.score || a.label.localeCompare(b.label, 'zh-CN'));
	if (axis.length > maxPoints) {
		axis = axis.slice(0, maxPoints);
	}
	return axis.map((item) => ({ key: item.key, label: item.label }));
};

const alignDimensionRowsToAxis = (rows, unifiedAxis) => {
	const rowMap = new Map(rows.map((row) => [row.key, row]));
	return unifiedAxis.map(({ key, label }) => rowMap.get(key) ?? { key, label, ...createEmptyPrimaryPhaseTotals() });
};

const accumulateDimensionOrder = (buckets, order, dimensionField) => {
	if (ORDER_PRIMARY_PHASE_LINE_ITEM_DIMENSIONS.has(dimensionField)) {
		const countedKeys = new Set();
		for (const line of resolveOrderLineItemDimensionBuckets(order, dimensionField)) {
			if (!buckets.has(line.key)) {
				buckets.set(line.key, { key: line.key, label: line.label, ...createEmptyPrimaryPhaseTotals() });
			}
			const incrementCount = !countedKeys.has(line.key);
			if (incrementCount) countedKeys.add(line.key);
			accumulatePrimaryPhaseOrderFinancialsPartial(buckets.get(line.key), order, {
				amount: line.amount,
				incrementCount,
			});
		}
		return;
	}

	const bucketKey = resolveOrderDimensionKey(order, dimensionField);
	const bucketLabel = resolveOrderDimensionLabel(order, dimensionField);
	if (!bucketKey) return;
	if (!buckets.has(bucketKey)) {
		buckets.set(bucketKey, { key: bucketKey, label: bucketLabel || bucketKey, ...createEmptyPrimaryPhaseTotals() });
	}
	accumulatePrimaryPhaseOrderFinancials(buckets.get(bucketKey), order);
};

/** 时间段内按维度汇总：X 轴为销售员/客户/助理/省份，Y 轴为单数或金额 */
export const buildLineCompareDimensionAxisSeries = ({
	orders = [],
	field = TREND_AXIS_MODE.SALE_USER,
	filters = {},
	saleUsers = [],
	assistantUsers = [],
	customerOptions = [],
	customerContactOptions = [],
	selectedPhase,
	startDate,
	endDate,
	maxPoints = DEFAULT_MAX_AXIS_POINTS,
}) => {
	const dimensionField = resolveLineCompareDimensionField(field);
	if (!dimensionField) {
		return { axisLabels: [], rows: [], fieldLabel: '' };
	}

	const fieldLabel = TREND_AXIS_FIELD_LABELS[field] ?? '维度';
	const filterKeys = resolveDimensionFilterKeys(field, filters, saleUsers, assistantUsers, customerOptions, customerContactOptions);
	const matchContext = { saleUsers, assistantUsers };
	const trendDateField = normalizeTrendDateField(filters.trendDateField);

	const buckets = new Map();
	if (filterKeys.length) {
		for (const item of filterKeys) {
			buckets.set(item.key, { key: item.key, label: item.label, ...createEmptyPrimaryPhaseTotals() });
		}
	}

	for (const order of orders ?? []) {
		if (selectedPhase && getOrderStatisticsPrimaryPhase(order) !== selectedPhase) continue;
		if (!orderInDateRange(order, startDate, endDate, trendDateField)) continue;
		const statsOrder = buildStatisticsMaterialScopedOrder(order, filters);

		if (filterKeys.length) {
			const matched = filterKeys.find((item) => orderMatchesDimensionFilterKey(order, field, item.key, matchContext));
			if (!matched) continue;
			if (ORDER_PRIMARY_PHASE_LINE_ITEM_DIMENSIONS.has(dimensionField)) {
				const countedKeys = new Set();
				for (const line of resolveOrderLineItemDimensionBuckets(statsOrder, dimensionField)) {
					if (line.key !== matched.key) continue;
					const prev = buckets.get(matched.key) ?? { key: matched.key, label: matched.label, ...createEmptyPrimaryPhaseTotals() };
					const incrementCount = !countedKeys.has(matched.key);
					if (incrementCount) countedKeys.add(matched.key);
					accumulatePrimaryPhaseOrderFinancialsPartial(prev, statsOrder, { amount: line.amount, incrementCount });
					buckets.set(matched.key, prev);
				}
			} else {
				const prev = buckets.get(matched.key) ?? { key: matched.key, label: matched.label, ...createEmptyPrimaryPhaseTotals() };
				accumulatePrimaryPhaseOrderFinancials(prev, statsOrder);
				buckets.set(matched.key, prev);
			}
			continue;
		}

		accumulateDimensionOrder(buckets, statsOrder, dimensionField);
	}

	let rows = [...buckets.values()].sort(
		(a, b) => getPrimaryPhaseRowMetricValue(b, 'totalPrice') - getPrimaryPhaseRowMetricValue(a, 'totalPrice') || b.count - a.count,
	);

	if (!filterKeys.length && rows.length > maxPoints) {
		rows = rows.slice(0, maxPoints);
	}

	return {
		axisLabels: rows.map((row) => row.label),
		rows,
		fieldLabel,
	};
};

/** 维度横轴：基准 + 对比段 → 多折线（每条线一个年份/区间，X 轴仍为对象维度） */
export const buildLineCompareDimensionChartData = ({
	orders = [],
	field = TREND_AXIS_MODE.SALE_USER,
	filters = {},
	saleUsers = [],
	assistantUsers = [],
	customerOptions = [],
	customerContactOptions = [],
	selectedPhase,
	maxPoints = resolveDimensionMaxAxisPoints(field),
}) => {
	const periods = resolveLineCompareDimensionPeriods(filters);
	const fieldLabel = TREND_AXIS_FIELD_LABELS[field] ?? '维度';
	const basePeriod = periods[0] ?? {
		id: 'all',
		kind: 'anchor',
		label: '全部',
		startDate: null,
		endDate: null,
	};

	if (periods.length <= 1) {
		const snapshot = buildLineCompareDimensionAxisSeries({
			orders,
			field,
			filters,
			saleUsers,
			assistantUsers,
			customerOptions,
			customerContactOptions,
			selectedPhase,
			startDate: basePeriod.startDate,
			endDate: basePeriod.endDate,
			maxPoints,
		});
		return { ...snapshot, isMultiPeriod: false, series: [] };
	}

	const filterKeys = resolveDimensionFilterKeys(field, filters, saleUsers, assistantUsers, customerOptions, customerContactOptions);
	const periodSnapshots = periods.map((period) =>
		buildLineCompareDimensionAxisSeries({
			orders,
			field,
			filters,
			saleUsers,
			assistantUsers,
			customerOptions,
			customerContactOptions,
			selectedPhase,
			startDate: period.startDate,
			endDate: period.endDate,
			maxPoints,
		}),
	);
	const unifiedAxis = resolveUnifiedDimensionAxis(
		filterKeys,
		periodSnapshots.map((snapshot) => snapshot.rows),
		maxPoints,
	);

	if (!unifiedAxis.length) {
		return { axisLabels: [], rows: [], fieldLabel, isMultiPeriod: false, series: [] };
	}

	const series = periods.map((period, index) => ({
		id: period.id,
		label: period.label,
		rows: alignDimensionRowsToAxis(periodSnapshots[index].rows, unifiedAxis),
	}));

	return {
		axisLabels: unifiedAxis.map((item) => item.label),
		axisKeys: unifiedAxis.map((item) => item.key),
		rows: series[0]?.rows ?? [],
		fieldLabel,
		isMultiPeriod: true,
		series,
	};
};

export const buildLineCompareDimensionAxisMetricSeries = (chartData, chartMetric) => {
	if (!chartData?.axisLabels?.length) return null;

	if (chartData.isMultiPeriod && chartData.series?.length) {
		return {
			axisLabels: chartData.axisLabels,
			axisKeys: chartData.axisKeys,
			rows: chartData.rows,
			fieldLabel: chartData.fieldLabel,
			isMultiPeriod: true,
			series: chartData.series.map((item) => ({
				label: item.label,
				values: item.rows.map((row) => getPrimaryPhaseRowMetricValue(row, chartMetric)),
			})),
		};
	}

	if (!chartData.rows?.length) return null;

	return {
		axisLabels: chartData.axisLabels,
		axisKeys: chartData.rows.map((row) => row.key),
		rows: chartData.rows,
		fieldLabel: chartData.fieldLabel,
		isMultiPeriod: false,
		values: chartData.rows.map((row) => getPrimaryPhaseRowMetricValue(row, chartMetric)),
	};
};

export const isTrendDimensionAxisMode = (mode) => Boolean(TREND_AXIS_TO_DIMENSION[mode]);

export const resolveLineCompareAxisDimension = (mode) => TREND_AXIS_TO_DIMENSION[mode] ?? null;
